// Service 7 — Company registration / foreign business licence / BOI.
// Corpus-free category definitions (client-bundle safe).

export type CompanyFaqCategory =
  | "basics"
  | "foreign-ownership"
  | "fbl-amity"
  | "boi"
  | "capital-visa"
  | "tax-registration"
  | "accounting-audit"
  | "branch-rep-office"
  | "documents-legalization"
  | "phuket-practical";

export interface CompanyCategoryDef {
  slug: CompanyFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const COMPANY_FAQ_CATEGORIES: CompanyCategoryDef[] = [
  {
    slug: "basics",
    titleTh: "พื้นฐานการจดทะเบียนบริษัทจำกัด — ขั้นตอนที่กรมพัฒนาธุรกิจการค้า",
    titleEn: "Thai Limited Company Basics — Steps at the DBD",
    descTh:
      "จองชื่อนิติบุคคล หนังสือบริคณห์สนธิ จำนวนผู้ถือหุ้นขั้นต่ำ วัตถุประสงค์ กรรมการผู้มีอำนาจ และการจดทะเบียนจัดตั้งภายในวันเดียวที่กรมพัฒนาธุรกิจการค้า",
    descEn:
      "Name reservation, memorandum of association, minimum shareholders, objectives, authorised directors and same-day incorporation at the DBD.",
    matchTh: /(จองชื่อ|บริคณห์สนธิ|ผู้ถือหุ้นขั้นต่ำ|วัตถุประสงค์|กรรมการผู้มีอำนาจ|จดทะเบียนจัดตั้ง|บริษัทจำกัด|ห้างหุ้นส่วน|ตราประทับ)/i,
    matchEn: /(name reservation|memorandum of association|minimum shareholder|objectives|authorised director|incorporat|limited company|partnership|company seal)/i,
  },
  {
    slug: "foreign-ownership",
    titleTh: "สัดส่วนหุ้นต่างชาติ — 49% นอมินี และโครงสร้างที่ถูกกฎหมาย",
    titleEn: "Foreign Shareholding — the 49% Line, Nominees and Lawful Structures",
    descTh:
      "พระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 บัญชีท้าย 1-2-3 การถือหุ้นเกิน 49% ความเสี่ยงเรื่องนอมินี และเครื่องมือกำกับสิทธิออกเสียงที่ทำได้จริง",
    descEn:
      "The Foreign Business Act 1999 lists, crossing 49%, nominee-shareholder risk, and control tools that are actually lawful.",
    matchTh: /(49%|สัดส่วนหุ้น|นอมินี|คนต่างด้าว|บัญชีท้าย|หุ้นบุริมสิทธิ|สิทธิออกเสียง|ถือหุ้นแทน)/i,
    matchEn: /(49%|shareholding ratio|nominee|foreign business act|list (one|two|three)|preference share|voting right)/i,
  },
  {
    slug: "fbl-amity",
    titleTh: "ใบอนุญาตประกอบธุรกิจของคนต่างด้าว (FBL) และสนธิสัญญาไทย-สหรัฐฯ",
    titleEn: "Foreign Business Licence (FBL) and the Thai–US Treaty of Amity",
    descTh:
      "เมื่อใดต้องขอ FBL หรือหนังสือรับรองการประกอบธุรกิจ ทุนขั้นต่ำ 2/3 ล้านบาทต่อธุรกิจ กรอบเวลาพิจารณา และสิทธิพิเศษของบริษัทสัญชาติอเมริกันตาม Treaty of Amity",
    descEn:
      "When an FBL or certificate is required, the 2–3 million baht minimum capital, review timelines, and US-owned company rights under the Treaty of Amity.",
    matchTh: /(ใบอนุญาตประกอบธุรกิจของคนต่างด้าว|FBL|หนังสือรับรองการประกอบธุรกิจ|Treaty of Amity|สนธิสัญญา|ทุนขั้นต่ำ 3 ล้าน|อเมริกัน)/i,
    matchEn: /(foreign business licence|foreign business license|FBL|certificate to operate|treaty of amity|minimum capital of (2|3) million|american)/i,
  },
  {
    slug: "boi",
    titleTh: "การส่งเสริมการลงทุน BOI — กิจการที่ได้รับส่งเสริม สิทธิประโยชน์ และขั้นตอนยื่น",
    titleEn: "BOI Promotion — Eligible Activities, Incentives and Filing Steps",
    descTh:
      "ประเภทกิจการที่ BOI ส่งเสริม สิทธิถือหุ้นต่างชาติ 100% สิทธิถือกรรมสิทธิ์ที่ดินเพื่อกิจการ ยกเว้นภาษีเงินได้นิติบุคคล และระบบ e-Investment / e-Expert",
    descEn:
      "BOI-eligible activity categories, 100% foreign ownership, land rights for promoted projects, corporate tax holidays, and the e-Investment / e-Expert systems.",
    matchTh: /(BOI|ส่งเสริมการลงทุน|e-?Investment|e-?Expert|ยกเว้นภาษีเงินได้นิติบุคคล|บัตรส่งเสริม|IEAT|นิคมอุตสาหกรรม)/i,
    matchEn: /(BOI|board of investment|e-?investment|e-?expert|corporate income tax exemption|promotion certificate|IEAT|industrial estate)/i,
  },
  {
    slug: "capital-visa",
    titleTh: "ทุนจดทะเบียนกับวีซ่าและใบอนุญาตทำงาน — ตัวเลขที่ต้องวางแผนตั้งแต่วันจัดตั้ง",
    titleEn: "Registered Capital vs Visa and Work Permit — Numbers to Plan on Day One",
    descTh:
      "ทุนจดทะเบียนชำระแล้ว 2 ล้านบาทต่อคนต่างชาติหนึ่งคน อัตราส่วนลูกจ้างไทย 4:1 การชำระค่าหุ้น หลักฐานทางธนาคาร และผลต่อการขออยู่ต่อ 1 ปี",
    descEn:
      "THB 2 million paid-up capital per foreign employee, the 4:1 Thai-staff ratio, share-payment evidence, and the effect on the 1-year extension of stay.",
    matchTh: /(ทุนจดทะเบียน|ชำระค่าหุ้น|2 ล้าน|4 ต่อ 1|ลูกจ้างไทย|ใบอนุญาตทำงาน|Non-?B|ขออยู่ต่อ)/i,
    matchEn: /(registered capital|paid-?up|2 million|4 thai|thai employee|work permit|Non-?B|extension of stay)/i,
  },
  {
    slug: "tax-registration",
    titleTh: "การจดทะเบียนภาษี — เลขผู้เสียภาษี VAT ภ.พ.20 และภาษีหัก ณ ที่จ่าย",
    titleEn: "Tax Registration — Tax ID, VAT (PP.20) and Withholding Tax",
    descTh:
      "เมื่อใดต้องจด VAT (รายรับเกิน 1.8 ล้านบาทต่อปี) แบบ ภ.พ.01 ภ.พ.20 การยื่น ภ.พ.30 รายเดือน ภาษีหัก ณ ที่จ่าย ภ.ง.ด.1/3/53 และประกันสังคม",
    descEn:
      "When VAT registration is mandatory (over THB 1.8m annual revenue), forms PP.01/PP.20, monthly PP.30, withholding returns PND.1/3/53 and social security.",
    matchTh: /(VAT|ภ\.?พ\.?(01|20|30)|ภาษีมูลค่าเพิ่ม|1\.8 ล้าน|หัก ณ ที่จ่าย|ภ\.?ง\.?ด|ประกันสังคม|เลขผู้เสียภาษี)/i,
    matchEn: /(VAT|PP\.?(01|20|30)|value added tax|1\.8 ?m|withholding|PND|social security|tax ?ID)/i,
  },
  {
    slug: "accounting-audit",
    titleTh: "บัญชี ผู้สอบบัญชี และการยื่นงบการเงินประจำปี",
    titleEn: "Accounting, Audit and Annual Financial Statement Filing",
    descTh:
      "หน้าที่จัดทำบัญชีตามพระราชบัญญัติการบัญชี พ.ศ. 2543 การประชุมสามัญผู้ถือหุ้น การยื่นงบการเงินผ่าน DBD e-Filing บัญชีรายชื่อผู้ถือหุ้น (บอจ.5) และ ภ.ง.ด.50/51",
    descEn:
      "Bookkeeping duties under the Accounting Act 2000, the AGM, DBD e-Filing of financial statements, the shareholder list (BorJor.5) and PND.50/51.",
    matchTh: /(งบการเงิน|ผู้สอบบัญชี|บอจ\.?5|e-?Filing|ประชุมสามัญ|ภ\.?ง\.?ด\.?(50|51)|พระราชบัญญัติการบัญชี|ปิดงบ)/i,
    matchEn: /(financial statement|auditor|BorJor\.?5|e-?filing|annual general meeting|PND\.?(50|51)|accounting act|bookkeep)/i,
  },
  {
    slug: "branch-rep-office",
    titleTh: "สำนักงานสาขา สำนักงานผู้แทน และสำนักงานภูมิภาค",
    titleEn: "Branch Office, Representative Office and Regional Office",
    descTh:
      "ความต่างระหว่างสาขา สำนักงานผู้แทน และสำนักงานภูมิภาค ขอบเขตกิจกรรมที่ห้ามหารายได้ ทุนนำเข้าขั้นต่ำ และเอกสารบริษัทแม่ที่ต้องรับรอง",
    descEn:
      "How a branch, representative office and regional office differ, the no-revenue activity limits, minimum remitted capital, and parent-company documents to legalise.",
    matchTh: /(สำนักงานสาขา|สำนักงานผู้แทน|สำนักงานภูมิภาค|บริษัทแม่|ไม่หารายได้|ทุนนำเข้า)/i,
    matchEn: /(branch office|representative office|regional office|parent company|non-?revenue|remitted capital)/i,
  },
  {
    slug: "documents-legalization",
    titleTh: "เอกสารบริษัทแม่จากต่างประเทศ — Notary, Apostille และคำแปล",
    titleEn: "Foreign Parent-Company Documents — Notary, Apostille and Translation",
    descTh:
      "ลำดับที่ถูกต้องของหนังสือรับรองบริษัทแม่ มติที่ประชุมกรรมการ หนังสือมอบอำนาจ การรับรองในประเทศต้นทาง การแปลเป็นไทย และการรับรองที่กรมการกงสุล",
    descEn:
      "The correct order for parent-company certificates, board resolutions and powers of attorney: origin-country certification, Thai translation, then consular legalisation.",
    matchTh: /(หนังสือรับรองบริษัทแม่|มติที่ประชุม|หนังสือมอบอำนาจ|Apostille|กรมการกงสุล|แปลเป็นไทย|Notary)/i,
    matchEn: /(parent-?company certificate|board resolution|power of attorney|apostille|consular affairs|thai translation|notar)/i,
  },
  {
    slug: "phuket-practical",
    titleTh: "จดบริษัทที่ภูเก็ต — ที่ตั้งสำนักงาน ธุรกิจท่องเที่ยว วิลล่า และใบอนุญาตเฉพาะ",
    titleEn: "Incorporating in Phuket — Office Address, Tourism, Villas and Sector Licences",
    descTh:
      "ที่ตั้งสำนักงานที่ใช้จดทะเบียนได้ในภูเก็ต ทะเบียนบ้านและหนังสือยินยอมให้ใช้สถานที่ ใบอนุญาตธุรกิจนำเที่ยว โรงแรม เรือเช่าเหมาลำ และงานที่สำนักงานพาณิชย์จังหวัดภูเก็ต",
    descEn:
      "Which Phuket addresses can be registered, house-book and landlord consent, tourism/hotel/charter licences, and filings at the Phuket provincial commerce office.",
    matchTh: /(ภูเก็ต|ทะเบียนบ้าน|หนังสือยินยอมให้ใช้สถานที่|ธุรกิจนำเที่ยว|โรงแรม|เรือเช่าเหมาลำ|สำนักงานพาณิชย์จังหวัด|วิลล่า)/i,
    matchEn: /(phuket|house (book|registration)|landlord consent|tour(ism)? licence|hotel licence|charter|provincial commerce office|villa)/i,
  },
];

export function getCompanyCategoryDef(slug: string): CompanyCategoryDef | undefined {
  return COMPANY_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
